import { useQuery, UseQueryResult } from "@tanstack/react-query"

export interface PerformanceMetrics {
  timestamp: string
  endpoint: string
  responseTime: number
  cacheHit: boolean
  jiraApiCalls: number
  errorRate: number
  userId?: string
}

export interface DashboardStats {
  totalRequests: number
  averageResponseTime: number
  cacheHitRate: number
  errorRate: number
  jiraApiCallsReduction: number
  activeUsers: number
}

export interface EndpointMetrics {
  endpoint: string
  totalRequests: number
  averageResponseTime: number
  p50ResponseTime: number
  p95ResponseTime: number
  p99ResponseTime: number
  cacheHitRate: number
  errorRate: number
  jiraApiCallsPerRequest: number
  lastUpdated: string
}

export interface PerformanceAlert {
  id: string
  type: "warning" | "critical"
  message: string
  timestamp: string
  endpoint?: string
  metric: string
  threshold: number
  actualValue: number
}

export interface DashboardData {
  timeRange: string
  startTime: string
  endTime: string
  stats: DashboardStats
  metrics: PerformanceMetrics[]
  endpointMetrics: EndpointMetrics[]
  alerts: PerformanceAlert[]
  lastUpdated: string
}

export interface HistoricalDataPoint {
  timestamp: string
  value: number | null
  count: number
  min?: number
  max?: number
  p95?: number
}

export interface PerformanceHistory {
  timeRange: string
  aggregation: string
  metric: string
  endpoint?: string
  startTime: string
  endTime: string
  data: HistoricalDataPoint[]
  trends: {
    direction: "increasing" | "decreasing" | "stable"
    changePercent: number
    isImproving: boolean | null
  }
  insights: string[]
  dataPoints: number
  lastUpdated: string
}

/**
 * Hook for fetching performance dashboard data
 */
export function usePerformanceDashboard(
  timeRange: "1h" | "24h" | "7d" | "30d" = "24h",
  refreshInterval: number = 30000
): UseQueryResult<DashboardData, Error> {
  return useQuery(
    ["performance", "dashboard", timeRange],
    async (): Promise<DashboardData> => {
      const response = await fetch(
        `/api/performance/dashboard?timeRange=${timeRange}`
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      return response.json()
    },
    {
      enabled: !!timeRange,
      refetchInterval: refreshInterval,
      staleTime: 25000, // Consider data stale after 25 seconds
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error.message.includes("HTTP 4")) {
          return false
        }
        return failureCount < 3
      },
    }
  )
}

/**
 * Hook for fetching performance history data
 */
export function usePerformanceHistory(
  timeRange: "1h" | "24h" | "7d" | "30d" | "90d" = "7d",
  aggregation: "minute" | "hour" | "day" = "hour",
  metric:
    | "responseTime"
    | "cacheHitRate"
    | "errorRate"
    | "jiraApiCalls" = "responseTime",
  endpoint?: string
): UseQueryResult<PerformanceHistory, Error> {
  return useQuery(
    ["performance", "history", timeRange, aggregation, metric, endpoint],
    async (): Promise<PerformanceHistory> => {
      const params = new URLSearchParams({
        timeRange,
        aggregation,
        metric,
      })

      if (endpoint) {
        params.append("endpoint", endpoint)
      }

      const response = await fetch(`/api/performance/history?${params}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      return response.json()
    },
    {
      enabled: !!timeRange && !!aggregation && !!metric,
      staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
      cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
      retry: (failureCount, error) => {
        if (error.message.includes("HTTP 4")) {
          return false
        }
        return failureCount < 2
      },
    }
  )
}

